import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import isEqual from "lodash/isEqual";

type Props = {
    action: {function: Function, payload: Object},
    storeKey: string,
    render: (data: any, isLoading: boolean, pagination?: any, params?: any) => any,
    onUpdate?: Function,
    alwaysReload?: boolean,
}

const usePrevious = <T extends any>(value: T): T | undefined => {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });

    return ref.current;
};

export const FetchPaginatedData = ({ action, storeKey, render, onUpdate, alwaysReload }: Props) => {
    const isLoading = useSelector((state: any) => state[storeKey].isLoading);
    const isUpdating = useSelector((state: any) => state[storeKey].isUpdating);
    const isDirty = useSelector((state: any) => state[storeKey].isDirty);
    const data = useSelector((state: any) => state[storeKey].data);
    const tstamp = useSelector((state: any) => state[storeKey].timestamp);
    const pagination = useSelector((state: any) => state[storeKey].paging);
    const prevProps = usePrevious({ action });

    const dispatch = useDispatch();

    // fetch the data
    const fetch = () => {
        if (action) {
            dispatch(action.function(...Object.values(action.payload)));
        }
    }

    useEffect(() => {
        // if no data or if alwaysReload flag is set, fetch a fresh data
        if (!tstamp || alwaysReload) {
            fetch();
        }

        if (data && data.length && onUpdate) { // if data already loaded update the parent
            onUpdate(data);
        }
    }, []);

    useEffect(() => {
        const isActionChanged = prevProps?.action?.payload && !isEqual(action.payload, prevProps?.action?.payload);
        const isDataChanged = (isDirty && !isLoading);

        if (isActionChanged || isDataChanged) { // re-fetch the data
            fetch();
        }

        // $FlowFixMe (flow doesn't understand that tstamp is injected through connect)
        if (tstamp > 0 && onUpdate) {
            onUpdate(data);
        }
    }, [action, isDirty, isLoading]);

    const paginationData = {
        currentPage: pagination? pagination.page : null,
        totalPages: pagination ? pagination.pages : null,
        totalResults: pagination ? pagination.total : null,
    };

    return render(data, isLoading, paginationData);
};

export default FetchPaginatedData;
